import React, { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PeopleAltOutlined as LeadsIcon } from '@mui/icons-material';
import ActiveFiltersBar from '../../../components/dashboard/ActiveFiltersBar';
import DataGridCard from '../../../components/dashboard/DataGridCard';
import LeadsGrid from '../../../components/dashboard/LeadsGrid';
import {
  LEAD_PARAMS,
  leadParamsFromSearch,
  useLeadParamChips,
} from '../../../components/dashboard/LeadsGrid/leadParams';
import PageLayout from '../../../components/dashboard/PageLayout';
import PageHeader from '../../../components/dashboard/PageHeader';

/**
 * Lista de leads do dashboard: a página que representa um grupo de leads.
 * Qualquer tela pode linkar para cá com filtros na URL (ver `leadParams.js`);
 * eles aparecem como chips removíveis acima da grid.
 */
export default function LeadsList() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.toString();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const params = useMemo(() => leadParamsFromSearch(searchParams), [search]);

  const removeParam = useCallback((name) => setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    next.delete(name);
    return next;
  }, { replace: true }), [setSearchParams]);

  const clearAll = useCallback(() => setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    LEAD_PARAMS.forEach((name) => next.delete(name));
    return next;
  }, { replace: true }), [setSearchParams]);

  const chips = useLeadParamChips(params, removeParam);

  const header = (
    <PageHeader
      icon={<LeadsIcon color="primary" />}
      title={t('nav.leads')}
      breadcrumbs={[
        { label: t('metrics.breadcrumb.dashboard') },
        { label: t('nav.groups.service') },
        { label: t('nav.leads') },
      ]}
    />
  );

  return (
    <PageLayout header={header}>
      <DataGridCard>
        <LeadsGrid
          params={params}
          toolbarExtra={(
            <ActiveFiltersBar
              chips={chips}
              clearLabel={t('leads.params.clear')}
              onClearAll={clearAll}
            />
          )}
        />
      </DataGridCard>
    </PageLayout>
  );
}
